import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import type { NoChildrenProp, RefElement, WithAttributes, TestIdProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useTestIds } from '../../hooks';
import { withTestIds } from '../../utils';

import { getAlertTestIds } from './Badges.test-ids';

export type AlertProps = WithAttributes<
  'div',
  NoChildrenProp &
    TestIdProp & {
      /** Determines the color to render the Badge as. This color is derived from the Theme. */
      variant: 'urgent' | 'success';
    }
>;

export const StyledAlert = styled.div<AlertProps>(props => {
  const alerts = props.theme.components.badges.alert;
  const { background } = alerts[props.variant];
  const { 'border-color': borderColor } = alerts.base;
  return css`
    background: ${background};
    border: 0.0625rem solid ${borderColor};
    border-radius: 100%;
    display: inline-block;
    height: 0.625rem;
    width: 0.625rem;
  `;
});

StyledAlert.defaultProps = defaultThemeProp;

export default withTestIds(
  forwardRef<RefElement<AlertProps>, PropsWithoutRef<AlertProps>>(function Alert(
    { testId, variant, ...restProps },
    ref
  ) {
    const testIds = useTestIds(testId, getAlertTestIds);

    return <StyledAlert data-testid={testIds.root} {...restProps} variant={variant} ref={ref} />;
  }),
  getAlertTestIds
);
